import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {
  Empty,
  Form,
  Input,
  Pagination,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import moment from 'moment';
import {
  EXPORT_MANAGED_FUND_INVENTORY_PREDICTION_RUN,
  GET_MANAGED_FUND_INVENTORY_PREDICTION_RESULTS,
  LIST_MANAGED_FUND_INVENTORY_PREDICTION_RUNS,
} from 'graphql/queries/assetManagement';
import {RUN_MANAGED_FUND_INVENTORY_PREDICTION} from 'graphql/mutations/assetManagement';
import DBModal from 'components/modal/modal';
import {formatEnumType, isDabaEmail} from 'utils/helper';
import {ManagedFundConfigs} from './ManagedFundConfigs';
import styles from './mutualFundsInventoryPrediction.module.scss';

const RUN_STATUS_OPTIONS = [
  {label: 'Running', value: 'running'},
  {label: 'Success', value: 'success'},
  {label: 'Partial', value: 'partial'},
  {label: 'Failed', value: 'failed'},
  {label: 'Superseded', value: 'superseded'},
];

const RUN_TYPE_OPTIONS = [
  {label: 'Scheduled', value: 'scheduled'},
  {label: 'Manual', value: 'manual'},
];

const statusColor = status =>
  ({
    SUCCESS: 'green',
    FAILED: 'red',
    PARTIAL: 'orange',
    RUNNING: 'blue',
    SUPERSEDED: 'default',
  }[String(status || '').toUpperCase()] || 'default');

// dataQuality arrives as a stringified JSON blob; parse it defensively.
const parseDataQuality = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {__raw: String(value)};
  }
};

// Count the entries in the parsed blob that flag a problem (truthy booleans
// and non-empty arrays like `warnings` / `skippedTickers`).
const countDataQualityIssues = parsed => {
  if (!parsed || parsed.__raw) return 0;
  return Object.values(parsed).reduce((acc, val) => {
    if (Array.isArray(val)) return acc + val.length;
    if (val === true) return acc + 1;
    return acc;
  }, 0);
};

const CALCULATION_METHOD_LABELS = {
  LAST_MONTH: 'Last 30 days',
  FULL_HISTORY_FALLBACK: 'Full history (fallback)',
};

const calculationMethodLabel = value =>
  CALCULATION_METHOD_LABELS[String(value || '').toUpperCase()] ||
  formatEnumType(value) ||
  '—';

const recommendationColor = recommendation =>
  ({
    ORDER: 'volcano',
    SUFFICIENT_INVENTORY: 'green',
    SKIPPED_STALE_DATA: 'default',
  }[String(recommendation || '').toUpperCase()] || 'blue');

const RECOMMENDATION_TOOLTIPS = {
  ORDER:
    'Inventory falls short of predicted demand; order the recommended units (demand + buffer).',
  SUFFICIENT_INVENTORY: 'Inventory covers predicted demand; no order needed.',
  SKIPPED_STALE_DATA:
    'NAV is stale (>5 business days); skipped until price refreshes.',
};

// Keyed by the data-quality field name normalised to lowercase alphanumerics,
// so it matches regardless of the exact casing/underscores the API returns.
const DATA_QUALITY_TOOLTIPS = {
  warnings:
    'Human-readable summary of every data-quality issue detected during this run.',
  tradesmissing:
    'Yes if no executed (FILLED/SETTLED) mutual-fund trades were found — historical demand could not be computed.',
  tradesstale:
    'Yes if the most recent executed trade is more than 7 days old, so demand may be out of date.',
  ordersmissing:
    'Yes if no open mutual-fund orders were found — current demand was treated as 0.',
  skippedtickers:
    'Funds excluded from recommendations because their NAV is stale (older than 5 business days).',
  negativeinventorytickers:
    'Funds whose available inventory came back negative — a data-integrity issue to investigate.',
  stalehistorytickers:
    'Funds with no trades in the last 7 days; their demand estimate is based on older activity.',
  missingfxtickers:
    'Funds with no FX rate for their currency — their USD equivalent could not be computed.',
  currencieswithoutthreshold:
    'Currencies with no configured high-priority threshold — runs in those currencies can never flag high priority.',
  id: 'Unique identifier for this run record (used for support and export).',
};

const normaliseKey = key =>
  String(key || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Column header text with an info icon that reveals the tooltip on hover.
const HeaderTip = ({label, tip}) => (
  <span className={styles.headerTip}>
    {label}
    <Tooltip title={tip}>
      <InfoCircleOutlined />
    </Tooltip>
  </span>
);

const formatNumber = value =>
  typeof value === 'number'
    ? value.toLocaleString(undefined, {maximumFractionDigits: 2})
    : '—';

const formatDateTime = value =>
  value ? moment(value).format('MMM DD, YYYY • HH:mm') : '—';

// Amounts are denominated in the asset's own currency — never assume XOF.
const formatAmount = (amount, currency) =>
  typeof amount === 'number'
    ? `${amount.toLocaleString(undefined, {maximumFractionDigits: 2})}${
        currency ? ` ${currency}` : ''
      }`
    : '—';

const formatUsd = amount =>
  typeof amount === 'number'
    ? `${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USD`
    : '—';

// FX rates are tiny (0.0017392 for XOF) — rounding to 2dp renders them as 0.00.
const formatFxRate = rate =>
  typeof rate === 'number'
    ? rate.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 8,
      })
    : '—';

export const MutualFundsInventoryPrediction = ({
  canRun = true,
  assetClassKey,
}) => {
  const [filterForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    status: undefined,
    runType: undefined,
  });
  const [openRunModal, setOpenRunModal] = useState(false);
  const [runEmail, setRunEmail] = useState('');
  const [dataQualityModal, setDataQualityModal] = useState({
    open: false,
    record: null,
  });
  const [runDetail, setRunDetail] = useState({open: false, run: null});
  const [openConfigs, setOpenConfigs] = useState(false);
  const adminId = useSelector(state => state?.auth?.userData?.user?.id);

  const buildFilter = (overrides = {}) => {
    const merged = {
      status: filters.status,
      runType: filters.runType,
      page: currentPage,
      limit,
      ...overrides,
    };
    const filter = {paginate: true, page: merged.page, limit: merged.limit};
    if (merged.status) filter.status = merged.status;
    if (merged.runType) filter.runType = merged.runType;
    return filter;
  };

  const {data, loading, refetch} = useQuery(
    LIST_MANAGED_FUND_INVENTORY_PREDICTION_RUNS,
    {
      variables: {filter: buildFilter()},
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onError: error => toast.error(error.message),
      onCompleted: res => {
        const result = res?.listManagedFundInventoryPredictionRuns;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
        }
      },
    },
  );

  const [runPrediction, {loading: runLoading}] = useMutation(
    RUN_MANAGED_FUND_INVENTORY_PREDICTION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [fetchResults, {data: resultsData, loading: resultsLoading}] =
    useLazyQuery(GET_MANAGED_FUND_INVENTORY_PREDICTION_RESULTS, {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
      onCompleted: res => {
        const result = res?.getManagedFundInventoryPredictionResults;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
        }
      },
    });

  const openRunDetail = run => {
    setRunDetail({open: true, run});
    if (run?.runId) {
      fetchResults({variables: {runId: run.runId}});
    }
  };

  const response = data?.listManagedFundInventoryPredictionRuns;
  const isError = response?.__typename === 'Error';
  const runs = isError ? [] : response?.data || [];
  const pagination = isError ? null : response?.pagination;

  const applyFilters = () => {
    const status = filterForm.getFieldValue('status');
    const runType = filterForm.getFieldValue('runType');
    setFilters({status, runType});
    setCurrentPage(1);
    refetch({filter: buildFilter({status, runType, page: 1})});
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setLimit(size);
    refetch({filter: buildFilter({page, limit: size})});
  };

  const handleRunNow = () => {
    const email = runEmail.trim();
    if (email && !isDabaEmail(email)) {
      toast.error('Use an official daba email address for the report');
      return;
    }
    runPrediction({variables: {triggeredBy: adminId, ...(email && {email})}})
      .then(({data}) => {
        const result = data?.runManagedFundInventoryPrediction;
        if (!result || result.__typename === 'Error') {
          toast.error(result?.message || 'Failed to trigger prediction run');
          return;
        }
        toast.success(
          result.message ||
            `Prediction run started${
              result?.data?.runId ? ` (${result.data.runId})` : ''
            }`,
        );
        setOpenRunModal(false);
        setRunEmail('');
        refetch({filter: buildFilter()});
      })
      .catch(error => toast.error(error.message));
  };

  const columns = [
    {
      title: (
        <HeaderTip
          label="Run Date"
          tip="The date the prediction run is for (the number below is the run ID)."
        />
      ),
      dataIndex: 'runDate',
      key: 'runDate',
      render: (value, record) => (
        <div className={styles.runDateCell}>
          <span>{value ? moment(value).format('MMM DD, YYYY') : '—'}</span>
          {record?.runId && <small>{record.runId}</small>}
        </div>
      ),
    },
    {
      title: (
        <HeaderTip
          label="Type"
          tip="How the run was triggered — Scheduled (weekly, Wed 15:00 GMT) or Manual (ad-hoc)."
        />
      ),
      dataIndex: 'runType',
      key: 'runType',
      render: value => (
        <Tag
          color={
            String(value || '').toUpperCase() === 'MANUAL'
              ? 'purple'
              : 'geekblue'
          }>
          {formatEnumType(value) || '—'}
        </Tag>
      ),
    },
    {
      title: (
        <HeaderTip
          label="Status"
          tip="Outcome of the run: Success (clean), Partial (completed with data-quality warnings), Running, Failed, or Superseded (replaced by a later same-day run)."
        />
      ),
      dataIndex: 'status',
      key: 'status',
      render: value => (
        <Tag color={statusColor(value)}>{formatEnumType(value) || '—'}</Tag>
      ),
    },
    {
      title: (
        <HeaderTip
          label="Recommended Amount"
          tip="Total value of all recommended fund orders in this run — the sum of each ordering fund's units x current NAV, per currency."
        />
      ),
      key: 'totalsByCurrency',
      render: (_, record) => {
        const totals = record?.totalsByCurrency || [];
        return (
          <div className={styles.totalsCell}>
            {totals.length ? (
              totals.map(total => (
                <span
                  key={total?.currency}
                  className={
                    total?.highPriority ? styles.totalHigh : undefined
                  }>
                  {formatAmount(total?.totalAmount, total?.currency)}
                </span>
              ))
            ) : (
              <span>—</span>
            )}
            {typeof record?.totalRecommendedAmountUsd === 'number' && (
              <small>≈ {formatUsd(record.totalRecommendedAmountUsd)}</small>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <HeaderTip
          label="High Priority"
          tip="Flagged when a currency's recommended total exceeds that currency's configured threshold."
        />
      ),
      dataIndex: 'highPriority',
      key: 'highPriority',
      render: value =>
        value ? (
          <Tag color="volcano">High Priority</Tag>
        ) : (
          <Tag color="default">Normal</Tag>
        ),
    },
    {
      title: (
        <HeaderTip
          label="Data Quality"
          tip="Number of data-quality warnings raised during the run; Clean means none. Click View for details."
        />
      ),
      dataIndex: 'dataQuality',
      key: 'dataQuality',
      render: (value, record) => {
        const parsed = parseDataQuality(value);
        if (!parsed) return '—';
        const issues = countDataQualityIssues(parsed);
        return (
          <div className={styles.dataQualityCell}>
            {parsed.__raw ? (
              <Tag color="default">Raw</Tag>
            ) : (
              <Tag color={issues ? 'red' : 'green'}>
                {issues ? `${issues} issue${issues > 1 ? 's' : ''}` : 'Clean'}
              </Tag>
            )}
            <Button
              type="secondary"
              label="View"
              onClick={() => setDataQualityModal({open: true, record})}
            />
          </div>
        );
      },
    },
    {
      title: 'Started / Completed',
      key: 'timing',
      render: (_, record) => (
        <div className={styles.timingCell}>
          <span>
            <label>Started</label>
            {formatDateTime(record?.startedAt)}
          </span>
          <span>
            <label>Completed</label>
            {formatDateTime(record?.completedAt)}
          </span>
        </div>
      ),
    },

    {
      title: 'Details',
      key: 'details',
      render: (_, record) => (
        <Button
          type="secondary"
          label="View Results"
          onClick={() => openRunDetail(record)}
        />
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2>Inventory Prediction Runs</h2>
          <p>
            Weekly demand forecasts and order recommendations for managed mutual
            funds. Review historical runs or trigger an ad-hoc run.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            type="secondary"
            label="Configuration"
            onClick={() => setOpenConfigs(true)}
          />
          <Button
            disabled={!canRun || runLoading}
            label="Run Now"
            onClick={() => setOpenRunModal(true)}
          />
        </div>
      </div>

      <div className={styles.controls}>
        <Form
          form={filterForm}
          layout="inline"
          className={styles.filterForm}
          onFinish={applyFilters}>
          <Form.Item name="status" label="Status">
            <Select
              allowClear
              placeholder="All statuses"
              style={{minWidth: 160}}
              options={RUN_STATUS_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="runType" label="Type">
            <Select
              allowClear
              placeholder="All types"
              style={{minWidth: 160}}
              options={RUN_TYPE_OPTIONS}
            />
          </Form.Item>
          <Form.Item>
            <Button type="secondary" label="Apply" onClick={applyFilters} />
          </Form.Item>
        </Form>
      </div>

      <div className={styles.tableScroll}>
        <Table
          loading={loading}
          dataSource={runs}
          rowKey={record => record?.id || record?.runId}
          pagination={false}
          className="antd-table"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No prediction runs found"
              />
            ),
          }}
          columns={columns}
        />
      </div>

      {pagination?.total ? (
        <Pagination
          className="ant-table-pagination"
          current={pagination?.page || currentPage}
          pageSize={limit}
          total={pagination?.total}
          showSizeChanger
          onChange={(page, size) => handlePageChange(page, size)}
        />
      ) : null}

      <DBModal
        isOpen={openRunModal}
        width={520}
        destroyOnClose
        handleClose={() => setOpenRunModal(false)}
        content={
          <div className={styles.exportModal}>
            <h2>Run Inventory Prediction</h2>
            <p>
              This triggers an ad-hoc mutual funds inventory prediction run now.
              A same-day re-run marks the earlier run as superseded.
            </p>
            <label className={styles.exportLabel}>
              Send this run&apos;s report to (optional)
            </label>
            <Input
              placeholder="Defaults to the configured finance team email"
              value={runEmail}
              onChange={event => setRunEmail(event.target.value)}
            />
            <div className={styles.exportActions}>
              <Button
                type="secondary"
                label="Cancel"
                onClick={() => setOpenRunModal(false)}
              />
              <Button
                disabled={runLoading}
                label={runLoading ? 'Starting…' : 'Run Now'}
                onClick={handleRunNow}
              />
            </div>
          </div>
        }
      />

      <DBModal
        isOpen={dataQualityModal.open}
        width={640}
        handleClose={() => setDataQualityModal({open: false, record: null})}
        content={
          <DataQualityContent
            record={dataQualityModal.record}
            onClose={() => setDataQualityModal({open: false, record: null})}
          />
        }
      />

      <DBModal
        isOpen={runDetail.open}
        width={1100}
        handleClose={() => setRunDetail({open: false, run: null})}
        content={
          <RunResultsContent
            run={runDetail.run}
            loading={resultsLoading}
            response={resultsData?.getManagedFundInventoryPredictionResults}
          />
        }
      />

      <DBModal
        isOpen={openConfigs}
        width={900}
        destroyOnClose
        handleClose={() => setOpenConfigs(false)}
        content={
          <ManagedFundConfigs
            open={openConfigs}
            assetClassKey={assetClassKey}
            canManage={canRun}
            onClose={() => setOpenConfigs(false)}
          />
        }
      />
    </div>
  );
};

const RunResultsContent = ({run, loading, response}) => {
  const isError = response?.__typename === 'Error';
  const results = isError ? [] : response?.data || [];

  const [showExport, setShowExport] = useState(false);
  const [recipients, setRecipients] = useState([]);

  const [exportRun, {loading: exporting}] = useLazyQuery(
    EXPORT_MANAGED_FUND_INVENTORY_PREDICTION_RUN,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleExport = () => {
    const emails = recipients.map(email => email.trim()).filter(Boolean);
    if (!emails.length) {
      toast.error('Add at least one recipient email');
      return;
    }
    const invalid = emails.filter(email => !isDabaEmail(email));
    if (invalid.length) {
      toast.error(
        `Use official daba emails only — invalid: ${invalid.join(', ')}`,
      );
      return;
    }
    exportRun({variables: {input: {id: run?.id, recipients: emails}}})
      .then(({data}) => {
        const result = data?.exportManagedFundInventoryPredictionRun;
        if (!result || result.__typename === 'Error') {
          toast.error(result?.message || 'Failed to export run');
          return;
        }
        toast.success(result.message || 'Export sent');
        setShowExport(false);
        setRecipients([]);
      })
      .catch(error => toast.error(error.message));
  };

  const columns = [
    {
      title: 'Ticker / Fund Name',
      dataIndex: 'ticker',
      key: 'ticker',
      render: (value, record) => (
        <div className={styles.runDateCell}>
          <span>{value || '—'}</span>
          {record?.dabaName && <small>{record.dabaName}</small>}
        </div>
      ),
    },
    {
      title: (
        <HeaderTip
          label="Currency"
          tip="The currency every amount on this row is denominated in."
        />
      ),
      dataIndex: 'currency',
      key: 'currency',
      render: value => <Tag color="blue">{value || '—'}</Tag>,
    },
    {
      title: 'Recommendation',
      dataIndex: 'recommendation',
      key: 'recommendation',
      render: value => {
        const tip = RECOMMENDATION_TOOLTIPS[String(value || '').toUpperCase()];
        const tag = (
          <Tag color={recommendationColor(value)}>
            {formatEnumType(value) || '—'}
          </Tag>
        );
        return tip ? <Tooltip title={tip}>{tag}</Tooltip> : tag;
      },
    },
    {
      title: (
        <HeaderTip
          label="Demand Basis"
          tip="Window of trade history the weekly demand estimate was computed from."
        />
      ),
      dataIndex: 'calculationMethod',
      key: 'calculationMethod',
      render: calculationMethodLabel,
    },
    {
      title: 'Weekly Avg Demand',
      dataIndex: 'weeklyAvgDemand',
      key: 'weeklyAvgDemand',
      render: formatNumber,
    },
    {
      title: 'Net Demand',
      dataIndex: 'currentNetDemand',
      key: 'currentNetDemand',
      render: formatNumber,
    },
    {
      title: 'Inventory',
      dataIndex: 'currentInventory',
      key: 'currentInventory',
      render: formatNumber,
    },
    {
      title: 'Ongoing',
      dataIndex: 'ongoingUnits',
      key: 'ongoingUnits',
      render: formatNumber,
    },
    {
      title: 'Qty Needed',
      dataIndex: 'quantityNeeded',
      key: 'quantityNeeded',
      render: formatNumber,
    },
    {
      title: 'Buffer Rate',
      dataIndex: 'bufferRate',
      key: 'bufferRate',
      render: value =>
        typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '—',
    },
    {
      title: 'Adjusted Units',
      dataIndex: 'adjustedUnits',
      key: 'adjustedUnits',
      render: formatNumber,
    },
    {
      title: 'NAV',
      key: 'nav',
      render: (_, record) => (
        <div className={styles.navCell}>
          <span>{formatNumber(record?.navPrice)}</span>
          {record?.navDate && (
            <small>{moment(record.navDate).format('MMM DD, YYYY')}</small>
          )}
        </div>
      ),
    },
    {
      title: (
        <HeaderTip
          label="Order Amount"
          tip="Recommended order value, in the fund's own currency."
        />
      ),
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      render: (value, record) => formatAmount(value, record?.currency),
    },
    {
      title: (
        <HeaderTip
          label="Order Amount (USD)"
          tip="USD equivalent of the order amount. Shows — when no FX rate was available for this currency."
        />
      ),
      dataIndex: 'orderAmountUsd',
      key: 'orderAmountUsd',
      render: value => formatUsd(value),
    },
    {
      title: (
        <HeaderTip
          label="FX Rate To USD"
          tip="Rate used to convert this fund's order amount into USD."
        />
      ),
      dataIndex: 'fxRateToUsd',
      key: 'fxRateToUsd',
      render: (value, record) => (
        <div className={styles.navCell}>
          <span>{formatFxRate(value)}</span>
          {record?.fxRateUpdatedAt && (
            <small>
              {moment(record.fxRateUpdatedAt).format('MMM DD, YYYY')}
            </small>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.runResultsModal}>
      <div className={styles.resultsHeader}>
        <div className={styles.dqHeader}>
          <h2>Prediction Results</h2>
          {run?.runId && (
            <small>
              Run {run.runId}
              {run?.runDate
                ? ` • ${moment(run.runDate).format('MMM DD, YYYY')}`
                : ''}
            </small>
          )}
        </div>
        <Button
          type="secondary"
          label="Export"
          onClick={() => setShowExport(true)}
        />
      </div>

      {loading ? (
        <div className={styles.resultsLoading}>
          <Spin />
          <p>Loading results...</p>
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <Table
            dataSource={results}
            rowKey={record => record?.id || record?.ticker}
            pagination={false}
            className="antd-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    isError
                      ? response?.message || 'Failed to load results'
                      : 'No per-fund results for this run'
                  }
                />
              ),
            }}
            columns={columns}
          />
        </div>
      )}

      <DBModal
        isOpen={showExport}
        width={520}
        zIndex={1100}
        destroyOnClose
        handleClose={() => setShowExport(false)}
        content={
          <div className={styles.exportModal}>
            <h2>Export Prediction Run</h2>
            <p>
              The run export is emailed to the recipients below. Only official
              daba emails (@dabafinance.com / @joindaba.com) are accepted.
            </p>
            <label className={styles.exportLabel}>Recipients</label>
            <Select
              mode="tags"
              open={false}
              className={styles.emailSelect}
              placeholder="ops@dabafinance.com"
              value={recipients}
              tokenSeparators={[',', ' ', ';']}
              onChange={setRecipients}
            />
            <div className={styles.exportActions}>
              <Button
                type="secondary"
                label="Cancel"
                onClick={() => setShowExport(false)}
              />
              <Button
                disabled={exporting}
                label={exporting ? 'Sending…' : 'Send Export'}
                onClick={handleExport}
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

const DataQualityContent = ({record}) => {
  const parsed = parseDataQuality(record?.dataQuality);

  const renderValue = value => {
    if (Array.isArray(value)) {
      if (!value.length) return <span className={styles.muted}>None</span>;
      return (
        <ul className={styles.dqList}>
          {value.map((item, index) => (
            <li key={index}>
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'boolean') {
      return (
        <Tag color={value ? 'volcano' : 'green'}>{value ? 'Yes' : 'No'}</Tag>
      );
    }
    if (value && typeof value === 'object') {
      return (
        <pre className={styles.dqPre}>{JSON.stringify(value, null, 2)}</pre>
      );
    }
    if (value === null || value === undefined || value === '') {
      return <span className={styles.muted}>—</span>;
    }
    return <span>{String(value)}</span>;
  };

  return (
    <div className={styles.dataQualityModal}>
      <div className={styles.dqHeader}>
        <h2>Data Quality</h2>
        {record?.runId && <small>Run {record.runId}</small>}
      </div>

      {!parsed ? (
        <p className={styles.muted}>No data quality information available.</p>
      ) : parsed.__raw ? (
        <pre className={styles.dqPre}>{parsed.__raw}</pre>
      ) : (
        <div className={styles.dqGrid}>
          {Object.entries(parsed).map(([field, value]) => {
            const tip = DATA_QUALITY_TOOLTIPS[normaliseKey(field)];
            return (
              <div key={field} className={styles.dqItem}>
                <label className={styles.dqLabel}>
                  {formatEnumType(field)}
                  {tip && (
                    <Tooltip title={tip}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  )}
                </label>
                <div className={styles.dqValue}>{renderValue(value)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
